/* eslint-disable */

/*
This component takes in a node tree as input:

The node tree is a JSON object with the following structure:
type Node = {
    type: "eventType", // it has other possible values like "condition", "action"
    payload: {}, // a dynamic object which has different schema for different event types
    linkedCampaigns: [
        {campaignID: "a UUID", countryID: "a number"},
    ],
    children: Array<Node>, // list of nodes
}

The component shows the node tree in a visual way. Each node is shown as a rounded rectangle with the following information:
- The type of the node (as title)
- The summarised payload of the node (we need a function to summarise the payload, for now just put a placeholder function)
- The linked campaigns of the node (show an interactive info icon, show this info when user clicks on it)

The component is responsive and is able to handle different screen sizes.

*/

import React, { useState, useMemo } from 'react';
import { Icon, Popup, Label, Button } from 'semantic-grab-ui/src';
import * as _ from 'lodash';
import RuleBuilder from 'components/rule-builder/RuleBuilder';
import { visualNodeTypes } from 'data/visualiser';

// Function to summarize payload based on node type
const summarizePayload = (payload, type, displayMeta) => {
  if (!payload || _.isEmpty(payload)) {
    return 'No configuration';
  }

  // Handle different node types
  switch (type) {
    case visualNodeTypes.event:
      return payload.eventType || 'Unnamed event';
    case visualNodeTypes.collapsedCondition:
      let conditionName = payload.variable;
      if (displayMeta && displayMeta.ruleTypes) {
        const ruleItem = displayMeta.ruleTypes.find(item => item.variableName === payload.variable);
        if (ruleItem) {
          conditionName = ruleItem.noun;
        }
      }
      // Conditions are now handled by RuleBuilder component in TreeNode
      return `Condition on ${conditionName}`;
    case visualNodeTypes.delay:
      return `Delay: ${payload.durationInMin} minutes`;
    case visualNodeTypes.actions:
      // Display list of action types from the payload array
      if (Array.isArray(payload)) {
        const actionTypes = payload.map(action => action.type).filter(Boolean);
        return actionTypes.length > 0 ? actionTypes.join(', ') : 'No action types';
      }
      return 'Invalid action format';
    case visualNodeTypes.action:
      return payload.name || 'Unnamed action';
    case visualNodeTypes.experiment: {
      // Handle experiment node - shows group percentages like ExperimentShow
      const treatmentPercentages = _.get(payload, 'groupPercentage', []);
      const controlPercentage = 100 - _.sum(treatmentPercentages);

      if (treatmentPercentages.length === 0) {
        return 'Experiment: No treatment groups defined';
      }

      const totalGroups = treatmentPercentages.length + 1; // +1 for control group
      return `Experiment: ${totalGroups} groups (Control: ${controlPercentage}%, Treatments: ${treatmentPercentages.join('%, ')}%)`;
    }
    case visualNodeTypes.experimentTreatment: {
      // Handle experiment treatment node - shows specific group info like ExperimentTreatmentShow
      const groupID = _.get(payload, 'groupID', 0);
      const isControlGroup = groupID === 0;

      // Additional info that might be available
      const groupName = _.get(payload, 'groupName', '');
      const description = _.get(payload, 'description', '');

      let groupInfo = isControlGroup ? 'Control Group' : `Treatment Group ${groupID}`;

      if (groupName) {
        groupInfo += ` (${groupName})`;
      }

      if (description && description.length > 0) {
        groupInfo += ` - ${description.substring(0, 50)}${description.length > 50 ? '...' : ''}`;
      }

      return groupInfo;
    }
    default: {
      // Fallback for unknown types
      const keys = Object.keys(payload);
      if (keys.length === 0) return 'Empty payload';
      if (keys.length === 1) return `${keys[0]}: ${String(payload[keys[0]]).substring(0, 30)}${String(payload[keys[0]]).length > 30 ? '...' : ''}`;
      return `${keys.length} configurations`;
    }
  }
};

// Get color based on node type
const getNodeColor = (type) => {
  const colorMap = {
    [visualNodeTypes.event]: '#1e88e5',
    [visualNodeTypes.condition]: '#fb8c00',
    [visualNodeTypes.collapsedCondition]: '#fb7100',
    [visualNodeTypes.action]: '#43a047',
    [visualNodeTypes.actions]: '#43a047', // Same green color for actions
    [visualNodeTypes.experiment]: '#9c27b0', // Purple for experiment nodes
    [visualNodeTypes.experimentTreatment]: '#673ab7', // Deep purple for experiment treatment nodes
    default: '#757575'
  };
  return colorMap[type] || colorMap.default;
};

// Calculate tree layout
const calculateTreeLayout = (nodeTree) => {
  const nodes = [];
  const connections = [];
  let nodeIdCounter = 0;

  const NODE_WIDTH = 320;
  const NODE_HEIGHT = 120;
  const HORIZONTAL_SPACING = 380; // Space between levels (left to right)
  const VERTICAL_SPACING = Math.max(280, NODE_HEIGHT + 160);   // Space between siblings (top to bottom) - increased for better visual separation
  const CONDITION_NODE_EXTRA_SPACING = 40; // Additional vertical spacing for condition nodes with RuleBuilder content

  // First pass: calculate subtree heights
  const calculateSubtreeHeight = (node) => {
    if (!node.children || node.children.length === 0) {
      return NODE_HEIGHT;
    }

    const childHeights = node.children.map(child => calculateSubtreeHeight(child));
    const totalChildHeight = childHeights.reduce((sum, height) => sum + height, 0);
    const baseChildSpacing = (node.children.length - 1) * (VERTICAL_SPACING - NODE_HEIGHT);

    // Add extra spacing for condition nodes
    const extraConditionSpacing = node.children.reduce((total, child) => {
      return total + (child.type === visualNodeTypes.condition ? CONDITION_NODE_EXTRA_SPACING : 0);
    }, 0);

    const totalSpacing = baseChildSpacing + extraConditionSpacing;

    return Math.max(NODE_HEIGHT, (totalChildHeight + totalSpacing));
  };

  const processNode = (node, level = 0, parentId = null, startY = 0) => {
    const nodeId = `node-${nodeIdCounter}`;
    nodeIdCounter += 1;

    // Calculate subtree height for this node
    const subtreeHeight = calculateSubtreeHeight(node);

    // Position this node in the center of its allocated space
    const x = level * HORIZONTAL_SPACING;
    const y = startY + ((subtreeHeight - NODE_HEIGHT) / 2);

    const nodeData = {
      id: nodeId,
      type: node.type,
      payload: node.payload,
      linkedCampaigns: node.linkedCampaigns,
      x: x,
      y: y,
      level: level,
      subtreeHeight: subtreeHeight
    };

    nodes.push(nodeData);

    // Create connection from parent to this node
    if (parentId) {
      const parentNode = nodes.find(n => n.id === parentId);
      if (parentNode) {
        connections.push({
          id: `connection-${parentId}-${nodeId}`,
          from: {
            id: parentId,
            x: parentNode.x + NODE_WIDTH,
            y: parentNode.y + (NODE_HEIGHT / 2)
          },
          to: {
            id: nodeId,
            x: x,
            y: y + (NODE_HEIGHT / 2)
          }
        });
      }
    }

    // Process children with proper vertical spacing
    if (node.children && node.children.length > 0) {
      let currentY = startY;
      node.children.forEach((child) => {
        const childSubtreeHeight = calculateSubtreeHeight(child);
        processNode(child, level + 1, nodeId, currentY);
        // Add extra spacing for condition nodes and between all siblings
        const nodeTypeSpacing = child.type === visualNodeTypes.condition ? CONDITION_NODE_EXTRA_SPACING : 0; // Extra space for condition nodes
        currentY += childSubtreeHeight + (VERTICAL_SPACING - NODE_HEIGHT) + nodeTypeSpacing;
      });
    }

    return subtreeHeight;
  };

  if (nodeTree) {
    processNode(nodeTree, 0, null, 0);
  }

  return { nodes, connections };
};

// Individual Node Component
const TreeNode = ({ nodeData, style, ruleTypes = {}, onShowNodeDetails }) => {
  const { type, payload, linkedCampaigns } = nodeData;
  const hasLinkedCampaigns = linkedCampaigns && linkedCampaigns.length > 0;

  // Check if this is an action node (either visualNodeTypes.action or visualNodeTypes.actions) or experiment node that might have detailed payload
  const isActionNode = type === visualNodeTypes.action || type === visualNodeTypes.actions;
  const isDetailNode = isActionNode || type === visualNodeTypes.experiment || type === visualNodeTypes.experimentTreatment || type === visualNodeTypes.collapsedCondition;

  const nodeStyle = {
    ...style,
    padding: '12px',
    borderRadius: '12px',
    border: `2px solid ${getNodeColor(type)}`,
    backgroundColor: '#ffffff',
    width: '320px', // Increased width for better RuleBuilder display
    minHeight: type === visualNodeTypes.condition ? '160px' : (type === visualNodeTypes.experiment || type === visualNodeTypes.experimentTreatment) ? '140px' : '120px', // Increased height for condition and experiment nodes
    maxHeight: type === visualNodeTypes.condition ? '200px' : (type === visualNodeTypes.experiment || type === visualNodeTypes.experimentTreatment) ? '160px' : '140px', // Increased max height for condition and experiment nodes
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    position: 'absolute',
    zIndex: 2,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  };

  const linkedCampaignsContent = hasLinkedCampaigns ? (
    <div>
      <strong>Linked Campaigns:</strong>
      <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
        {linkedCampaigns.map((campaign, index) => (
          <li key={index} style={{ fontSize: '0.9em', marginBottom: '3px' }}>
            <a
              href={`${window.Grab.config.OMEGA_RTC_URI}/campaigns/${campaign.countryID}/${campaign.campaignID}/edit`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#1e88e5', textDecoration: 'underline' }}
            >
              {campaign.campaignName.length > 18
                ? `${campaign.campaignName.substring(0, 18)}...`
                : campaign.campaignName
              }
            </a>
          </li>
        ))}
      </ul>
    </div>
  ) : (
    'No linked campaigns'
  );



  return (
    <div style={nodeStyle}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '8px',
        flexShrink: 0
      }}>
        <Label
          style={{
            backgroundColor: getNodeColor(type),
            color: 'white',
            fontSize: '0.8em',
            padding: '4px 8px',
            borderRadius: '4px'
          }}
        >
          {type}
        </Label>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Info icon for linked campaigns */}
          {hasLinkedCampaigns && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Popup
            trigger={
              <Icon
                name="info circle"
                color="blue"
                style={{ cursor: 'pointer' }}
              />
            }
                content={linkedCampaignsContent}
            position="top center"
                on="click"
                hideOnScroll
              />
              <span style={{
                fontSize: '0.75em',
                color: '#666',
                fontWeight: '500',
                backgroundColor: '#f0f0f0',
                padding: '2px 6px',
                borderRadius: '10px',
                border: '1px solid #ddd',
                lineHeight: '1'
              }}>
                ×{linkedCampaigns.length}
              </span>
            </div>
          )}
        </div>
      </div>

      <div style={{
        fontSize: '0.9em',
        color: '#666',
        lineHeight: '1.4',
        flex: 1,
        overflow: 'hidden'
      }}>
        {type === visualNodeTypes.condition ? (
          <div style={{
            maxHeight: '150px',
            overflow: 'auto', // Allow scrolling when content exceeds maxHeight
            fontSize: '0.85em', // Slightly smaller font for better fit
            border: '1px solid #e0e0e0',
            borderRadius: '4px',
            padding: '8px',
            backgroundColor: '#fafafa'
          }}>
            {ruleTypes && Array.isArray(ruleTypes) && ruleTypes.length > 0 ? (
              <RuleBuilder
                defaultValue={payload}
                readonly={true}
                config={{}}
                type="tridentCampaign"
                ruleTypes={ruleTypes}
                ruleTypesPassedFromSystem={true}
              />
            ) : (
              <div style={{ color: '#666', fontSize: '0.9em', fontStyle: 'italic' }}>
                {JSON.stringify(payload, null, 2)}
              </div>
            )}
          </div>
        ) : (
          <div>
            <div style={{
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              display: '-webkit-box',
              WebkitLineClamp: 4,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              marginBottom: isDetailNode ? '8px' : '0'
            }}>
        {summarizePayload(payload, type, {
          ruleTypes: ruleTypes,
        })}
            </div>

            {/* Details button for action and experiment nodes - positioned below the content */}
            {isDetailNode && (
              <Button
                size="mini"
                color={isActionNode ? "green" : "purple"}
                style={{
                  fontSize: '0.7em',
                  padding: '4px 8px',
                  height: 'auto'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onShowNodeDetails && onShowNodeDetails(payload);
                }}
              >
                Details
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// SVG Lines Component
const ConnectionLines = ({ connections, offsetX, offsetY, zoomLevel = 1 }) => {
  return (
    <svg
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 1
      }}
    >
      {connections.map((connection) => {
        const x1 = connection.from.x * zoomLevel + offsetX;
        const y1 = connection.from.y * zoomLevel + offsetY;
        const x2 = connection.to.x * zoomLevel + offsetX;
        const y2 = connection.to.y * zoomLevel + offsetY;

        // Create curved path for better visual appeal
        const midY = y1 + ((y2 - y1) / 2);
        const pathData = `M ${x1} ${y1} C ${x1} ${midY}, ${x2} ${midY}, ${x2} ${y2}`;

        return (
          <path
            key={connection.id}
            d={pathData}
            stroke="#888"
            strokeWidth={2 * zoomLevel}
            fill="none"
            markerEnd="url(#arrowhead)"
          />
        );
      })}

      {/* Arrow marker definition */}
      <defs>
        <marker
          id="arrowhead"
          markerWidth="10"
          markerHeight="7"
          refX="9"
          refY="3.5"
          orient="auto"
        >
          <polygon
            points="0 0, 10 3.5, 0 7"
            fill="#888"
          />
        </marker>
      </defs>
    </svg>
  );
};

// Custom Modal Component for Node Details
const NodeDetailsModal = ({ isOpen, onClose, payload }) => {
  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          maxWidth: '700px',
          maxHeight: '80vh',
          width: '100%',
          overflow: 'hidden',
          position: 'relative'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with title and close button */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px',
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#f8f9fa'
        }}>
          <h3 style={{ margin: 0, fontSize: '1.1em', fontWeight: '600' }}>Node Details</h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              fontWeight: 'bold',
              cursor: 'pointer',
              color: '#666',
              padding: '0',
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s',
              lineHeight: '1'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#f0f0f0';
              e.target.style.color = '#333';
              e.target.style.transform = 'scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = '#666';
              e.target.style.transform = 'scale(1)';
            }}
            title="Close"
          >
            ×
          </button>
        </div>

        {/* JSON content */}
        <div style={{
          padding: '20px',
          maxHeight: 'calc(80vh - 80px)',
          overflow: 'auto'
        }}>
          <pre style={{
            backgroundColor: '#f5f5f5',
            padding: '16px',
            borderRadius: '6px',
            fontSize: '0.9em',
            margin: '0',
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
            border: '1px solid #e0e0e0',
            fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace'
          }}>
            {JSON.stringify(payload, null, 2)}
          </pre>
        </div>
      </div>
    </div>
  );
};

export default function CampaignsVisualiser({ nodeTree, ruleTypes = {} }) {
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [zoomLevel, setZoomLevel] = useState(1);
  const [modalState, setModalState] = useState({ isOpen: false, payload: null });
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Modal handler functions
  const handleShowNodeDetails = (payload) => {
    setModalState({ isOpen: true, payload });
  };

  const handleCloseModal = () => {
    setModalState({ isOpen: false, payload: null });
  };

  // Add keyboard support for closing modal and fullscreen
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && modalState.isOpen) {
        handleCloseModal();
      } else if (e.key === 'F11') {
        e.preventDefault();
        handleToggleFullscreen();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [modalState.isOpen, isFullscreen]);

  // Transform the tree data to layout format
  const layoutData = useMemo(() => {
    if (!nodeTree) {
      return { nodes: [], connections: [] };
    }
    return calculateTreeLayout(nodeTree);
  }, [nodeTree]);

  // Calculate auto-fit zoom level
  const calculateAutoFitZoom = () => {
    if (layoutData.nodes.length === 0) return 1;

    const minX = Math.min(...layoutData.nodes.map(n => n.x));
    const maxX = Math.max(...layoutData.nodes.map(n => n.x));
    const minY = Math.min(...layoutData.nodes.map(n => n.y));
    const maxY = Math.max(...layoutData.nodes.map(n => n.y));

    // Add some padding around the content
    const PADDING = 100;
    const contentWidth = (maxX - minX) + PADDING * 2;
    const contentHeight = (maxY - minY) + PADDING * 2;

    // Get container dimensions
    const containerWidth = isFullscreen ? window.innerWidth : 800;
    const containerHeight = isFullscreen ? window.innerHeight : 600;

    // Calculate zoom to fit both width and height
    const zoomToFitWidth = containerWidth / contentWidth;
    const zoomToFitHeight = containerHeight / contentHeight;

    // Use the smaller zoom to ensure everything fits
    const autoFitZoom = Math.min(zoomToFitWidth, zoomToFitHeight);

    // Clamp between min and max zoom levels
    return Math.min(Math.max(autoFitZoom, 0.3), 3);
  };

  // Calculate pan offset to center the event node
  const calculateEventNodeCenterOffset = (zoomLevel) => {
    const eventNode = layoutData.nodes.find(node => node.type === visualNodeTypes.event || node.type === 'eventType');
    if (!eventNode) return { x: 0, y: 0 };

    const containerWidth = isFullscreen ? window.innerWidth : 800;
    const containerHeight = isFullscreen ? window.innerHeight : 600;

    // Calculate pan offset to center the event node
    const centerX = containerWidth / 2;
    const centerY = containerHeight / 2;

    const eventNodeCenterX = (eventNode.x + 160) * zoomLevel; // 160 = half node width
    const eventNodeCenterY = (eventNode.y + 60) * zoomLevel;  // 60 = half node height

    return {
      x: centerX - eventNodeCenterX,
      y: centerY - eventNodeCenterY
    };
  };

  // Auto-fit zoom level when layout changes
  React.useEffect(() => {
    const autoFitZoom = calculateAutoFitZoom();
    setZoomLevel(autoFitZoom);

    // Center the view on the event node
    const centerOffset = calculateEventNodeCenterOffset(autoFitZoom);
    setPanOffset(centerOffset);
  }, [layoutData.nodes, isFullscreen]);

  // Calculate container dimensions
  const containerDimensions = useMemo(() => {
    if (layoutData.nodes.length === 0) {
      return { width: 800, height: isFullscreen ? window.innerHeight : 600 };
    }

    const minX = Math.min(...layoutData.nodes.map(n => n.x));
    const maxX = Math.max(...layoutData.nodes.map(n => n.x));
    const minY = Math.min(...layoutData.nodes.map(n => n.y));
    const maxY = Math.max(...layoutData.nodes.map(n => n.y));

    const defaultWidth = isFullscreen ? window.innerWidth : 800;
    const defaultHeight = isFullscreen ? window.innerHeight : 600;

    const width = Math.max(defaultWidth, (maxX - minX) + 600);
    const height = Math.max(defaultHeight, (maxY - minY) + 300);

    return { width, height, minX, minY };
  }, [layoutData.nodes, isFullscreen]);

  // Center the tree initially - focus on centering the event node vertically
  const currentHeight = isFullscreen ? window.innerHeight : 600;

  // Find the event node (root node) position
  const eventNode = layoutData.nodes.find(node => node.type === visualNodeTypes.event || node.type === 'eventType');
  const eventNodeY = eventNode ? eventNode.y : 0;

  const initialOffset = {
    x: 50, // Start from left edge with some padding
    y: (currentHeight / 2) - eventNodeY - 60 // Center the event node in viewport (60 = half node height)
  };

  const handleMouseDown = (e) => {
    // Don't start dragging if clicking on a button or modal
    if (e.target.closest('.ui.button') || modalState.isOpen) {
      return;
    }

    setIsDragging(true);
    setDragStart({
      x: e.clientX - panOffset.x,
      y: e.clientY - panOffset.y
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging || modalState.isOpen) return;

    setPanOffset({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev * 1.2, 3)); // Max zoom 3x
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev / 1.2, 0.3)); // Min zoom 0.3x
  };

  const handleResetView = () => {
    const autoFitZoom = calculateAutoFitZoom();
    setZoomLevel(autoFitZoom);

    // Center the view on the event node
    const centerOffset = calculateEventNodeCenterOffset(autoFitZoom);
    setPanOffset(centerOffset);
  };

  const handleWheel = (e) => {
    // Prevent default scrolling behavior
    e.preventDefault();

    // Don't zoom if modal is open
    if (modalState.isOpen) return;

    // Check if this is a zoom gesture
    // Ctrl+wheel, or trackpad pinch gestures typically have ctrlKey set
    // Also check for significant vertical movement (mouse wheel or trackpad scroll)
    const isZoomGesture = e.ctrlKey || (Math.abs(e.deltaY) > Math.abs(e.deltaX));

    if (isZoomGesture) {
      // Calculate zoom factor based on wheel delta
      // Use a smaller factor for smoother zooming, especially on trackpads
      const sensitivity = e.ctrlKey ? 0.002 : 0.001; // More sensitive when Ctrl is pressed
      const deltaZoom = -e.deltaY * sensitivity; // Negative because wheel down should zoom out
      const maxZoom = 3;
      const minZoom = 0.3;

      setZoomLevel(prev => {
        const newZoom = prev * (1 + deltaZoom);
        return Math.min(Math.max(newZoom, minZoom), maxZoom);
      });
    }
  };

  const handleToggleFullscreen = () => {
    const element = document.querySelector('.campaign-visualiser-container');

    if (!isFullscreen) {
      // Enter fullscreen
      if (element.requestFullscreen) {
        element.requestFullscreen().catch(err => {
          console.warn('Could not enter fullscreen mode:', err);
        });
      } else if (element.webkitRequestFullscreen) {
        element.webkitRequestFullscreen();
      } else if (element.mozRequestFullScreen) {
        element.mozRequestFullScreen();
      } else if (element.msRequestFullscreen) {
        element.msRequestFullscreen();
      } else {
        // Fallback: simulate fullscreen by expanding container
        console.warn('Fullscreen API not supported, using fallback');
        setIsFullscreen(true);
        return;
      }
    } else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(err => {
          console.warn('Could not exit fullscreen mode:', err);
        });
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      } else {
        // Fallback: restore normal size
        setIsFullscreen(false);
        return;
      }
    }
  };

  // Listen for fullscreen changes to sync state
  React.useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );
      setIsFullscreen(isCurrentlyFullscreen);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  if (!nodeTree) {
    return (
      <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
        No campaign tree to display
      </div>
    );
  }

  const offsetX = (panOffset.x + initialOffset.x) * zoomLevel;
  const offsetY = (panOffset.y + initialOffset.y) * zoomLevel;

  return (
    <div
      className="campaign-visualiser-container"
      style={{
        width: '100%',
        height: isFullscreen ? '100vh' : '600px',
        border: isFullscreen ? 'none' : '1px solid #ddd',
        borderRadius: isFullscreen ? '0' : '8px',
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: '#f9f9f9',
        cursor: isDragging ? 'grabbing' : 'grab',
        zIndex: 1,
        transition: 'all 0.3s ease-in-out'
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onWheel={handleWheel}
    >
      {/* Background pattern */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundImage: 'radial-gradient(circle, #ddd 1px, transparent 1px)',
          backgroundSize: '20px 20px',
          opacity: 0.3
        }}
      />

      {/* Connection lines */}
      <ConnectionLines
        connections={layoutData.connections}
        offsetX={offsetX}
        offsetY={offsetY}
        zoomLevel={zoomLevel}
      />

      {/* Nodes */}
      {layoutData.nodes.map((nodeData) => (
        <TreeNode
          key={nodeData.id}
          nodeData={nodeData}
          ruleTypes={ruleTypes}
          onShowNodeDetails={handleShowNodeDetails}
          style={{
            left: nodeData.x * zoomLevel + offsetX,
            top: nodeData.y * zoomLevel + offsetY,
            transform: `scale(${zoomLevel})`,
            transformOrigin: 'top left'
          }}
        />
      ))}

      {/* Controls overlay */}
      <div style={{
        position: 'absolute',
        bottom: '10px',
        right: '10px',
        backgroundColor: 'rgba(255,255,255,0.9)',
        padding: '8px',
        borderRadius: '4px',
        fontSize: '0.8em',
        color: '#666',
        zIndex: 100  // Ensure controls overlay is always on top
      }}>
        Drag to pan • Zoom: {Math.round(zoomLevel * 100)}% • Pinch to zoom {isFullscreen ? '• Fullscreen' : ''}
      </div>

      {/* Zoom controls */}
      <div style={{
        position: 'absolute',
        top: '10px',
        right: '10px',
        display: 'flex',
        flexDirection: 'column',
        gap: '5px',
        zIndex: 100  // Ensure zoom controls are always on top
      }}>
        <button
          onClick={handleZoomIn}
          style={{
            width: '40px',
            height: '40px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            backgroundColor: 'rgba(255,255,255,0.9)',
            cursor: 'pointer',
            fontSize: '18px',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          title="Zoom In"
        >
          +
        </button>
        <button
          onClick={handleZoomOut}
          style={{
            width: '40px',
            height: '40px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            backgroundColor: 'rgba(255,255,255,0.9)',
            cursor: 'pointer',
            fontSize: '18px',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          title="Zoom Out"
        >
          −
        </button>
        <button
          onClick={handleResetView}
          style={{
            width: '40px',
            height: '40px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            backgroundColor: 'rgba(255,255,255,0.9)',
            cursor: 'pointer',
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          title="Reset View"
        >
          ⌂
        </button>
        <button
          onClick={handleToggleFullscreen}
          style={{
            width: '40px',
            height: '40px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            backgroundColor: 'rgba(255,255,255,0.9)',
            cursor: 'pointer',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
        >
          {isFullscreen ? '⊟' : '⊞'}
        </button>
      </div>

      {/* Node Details Modal */}
      <NodeDetailsModal
        isOpen={modalState.isOpen}
        onClose={handleCloseModal}
        payload={modalState.payload}
      />
    </div>
  );
}
